import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Briefcase } from 'lucide-react';
import { jobApplicationApi } from '@/lib/api/jobApplicationApi';
import { JobApplication, ApplicationStatusLabels, ApplicationStatusColors } from '@/lib/models/jobApplication';
import { LoadingState, EmptyState } from '@/components/custom-ui/empty-state';
import { StatusBadge } from '@/components/custom-ui/status-badge';
import { EntityAvatar } from '@/components/custom-ui/EntityAvatar';
import { NEXILINK_CARD_WHITE_ELEVATED, NEXILINK_ROW, NEXILINK_SPACING } from '@/lib/nexilink-card-styles';

const MyApplicationsPage: React.FC = () => {
    const navigate = useNavigate();
    const [applications, setApplications] = useState<JobApplication[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        loadApplications();
    }, []);

    const loadApplications = async () => {
        setIsLoading(true);
        try {
            const result = await jobApplicationApi.getMyApplications(1, 50);
            setApplications(result.applications);
        } finally {
            setIsLoading(false);
        }
    };

    const handleWithdraw = async (applicationId: string) => {
        try {
            await jobApplicationApi.withdrawApplication(applicationId);
            await loadApplications();
        } catch (err) {
            console.error('Failed to withdraw application:', err);
        }
    };

    if (isLoading) {
        return <LoadingState message="Laddar dina ansökningar..." />;
    }

    return (
        <div className={NEXILINK_SPACING}>
            {applications.length > 0 ? (
                <div className={NEXILINK_CARD_WHITE_ELEVATED}>
                    <div className="px-2 py-2 divide-y divide-gray-50">
                        {applications.map((app) => (
                            <div key={app.id}>
                                <div className={NEXILINK_ROW}>
                                    <EntityAvatar icon={FileText} size="md" orgName={app.organisation_name} orgLogoUrl={app.organisation_logo_url} />
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-semibold text-gray-900 truncate">{app.need_title || app.need_role || 'Tjänst'}</p>
                                        {(app.organisation_name || app.unit_name || app.need_role) && (
                                            <p className="text-sm text-gray-500 truncate">
                                                {[app.need_role, app.organisation_name, app.unit_name].filter(Boolean).join(' · ')}
                                            </p>
                                        )}
                                        <p className="text-xs text-gray-400">
                                            Skickad {new Date(app.applied_at).toLocaleDateString('sv-SE')}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-3 flex-shrink-0">
                                        <StatusBadge status={app.status} label={ApplicationStatusLabels[app.status]} />
                                        {app.status === 'accepted' && (
                                            <button
                                                onClick={() => navigate('/dashboard/my-workplace')}
                                                className="text-sm text-[#1A4D4F] hover:underline font-medium whitespace-nowrap"
                                            >
                                                Se din arbetsplats →
                                            </button>
                                        )}
                                        {(app.status === 'pending' || app.status === 'reviewing') && (
                                            <button
                                                onClick={() => handleWithdraw(app.id)}
                                                className="text-sm text-red-600 hover:text-red-700 font-medium"
                                            >
                                                Återkalla
                                            </button>
                                        )}
                                    </div>
                                </div>
                                {app.decision_note && (
                                    <p className="mx-4 mb-3 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                                        {app.decision_note}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <EmptyState
                    icon={FileText}
                    title="Inga ansökningar ännu"
                    description="Bläddra bland lediga tjänster och skicka din första ansökan!"
                    action={{ label: 'Bläddra bland jobb', icon: Briefcase, onClick: () => navigate('/dashboard/jobs') }}
                />
            )}
        </div>
    );
};

export default MyApplicationsPage;
